/**
 * brightside — behaviour for the bs- sections.
 *
 * - <bs-pack-picker>: keeps price, Save %, gift box, Add to bag label, ?variant= and the sticky bar
 *   in sync with the selected pack.
 * - form[data-bs-add]: Ajax add to bag that hands off to Horizon's cart drawer by dispatching the
 *   same CartLinesUpdateEvent Horizon's own product form uses (the drawer auto-opens and the cart
 *   count updates). Without this script the forms still post to /cart/add.
 * - <bs-sticky-bar>: mobile bar that mirrors the page's primary picker.
 * - Header: live cart count, cart drawer trigger, mobile menu dialog.
 * - <bs-gallery>: product gallery (thumbnails swap the main image; swipe carousel on mobile; zoom).
 * - Hero demo video: swaps the poster for the uploaded video on play.
 */

const routes = typeof Theme !== 'undefined' && Theme.routes ? Theme.routes : {};
const CART_ADD_URL = routes.cart_add_url || '/cart/add.js';
const CART_UPDATE_URL = `${routes.cart_update_url || '/cart/update'}.js`;
const CART_URL = routes.cart_url || '/cart';

/** CartLinesUpdateEvents dispatched by this script, so the gift sync doesn't react to its own changes. */
const ownEvents = new WeakSet();

/** @type {Promise<any> | null} */
let shopifyEventsPromise = null;

/** Horizon's cart drawer and cart count listen for events from this module (CDN, via the import map). */
function loadShopifyEvents() {
  shopifyEventsPromise ??= import('@shopify/events').catch(() => null);
  return shopifyEventsPromise;
}

/* ------------------------------------------------------------------ */
/* Add to bag                                                          */
/* ------------------------------------------------------------------ */

async function fetchCart() {
  const response = await fetch(`${CART_URL}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
  return response.json();
}

/** @param {string} url @param {object} payload */
async function postCart(url, payload) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify(payload),
  });
  return response.json();
}

/** Section ids of Horizon's cart-items components (drawer, cart page) for the Section Rendering API. */
function cartSectionIds() {
  return [
    ...new Set(
      [...document.querySelectorAll('cart-items-component')]
        .map((element) => element instanceof HTMLElement && element.dataset.sectionId)
        .filter(Boolean)
    ),
  ];
}

/**
 * Free gifts per pack variant, from snippets/bs-gift-map.liquid.
 * @returns {Record<string, Array<{ id: number, key: string }>>}
 */
function giftMap() {
  try {
    return JSON.parse(document.getElementById('bs-gift-map')?.textContent || '{}');
  } catch {
    return {};
  }
}

/**
 * Adds a pack (plus the free gifts it includes) to the cart and lets Horizon's cart drawer,
 * cart items and cart icon react. The gift lines carry the hidden `_bs_gift` property; the store's
 * automatic discounts make them $0.
 * @param {{ variantId: string, quantity?: number, source: HTMLElement, productId?: string }} options
 */
async function addToBag({ variantId, quantity = 1, source, productId = '' }) {
  const events = await loadShopifyEvents();
  const drawer = document.getElementById('cart-drawer');
  const sectionIds = cartSectionIds();

  const packLine = { id: Number(variantId), quantity };
  const giftLines = (giftMap()[String(variantId)] || []).map((gift) => ({
    id: gift.id,
    quantity,
    properties: { _bs_gift: gift.key },
  }));
  /** @param {object[]} items */
  const payload = (items) => (sectionIds.length ? { items, sections: sectionIds.join(',') } : { items });

  const deferred = events ? events.CartLinesUpdateEvent.createPromise() : null;

  if (events && deferred) {
    const event = new events.CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [packLine, ...giftLines].map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
      promise: deferred.promise,
    });
    ownEvents.add(event);
    source.dispatchEvent(event);
  }

  const detail = {
    source: 'bs-pack-picker',
    sourceId: source.id || 'bs-pack-picker',
    itemCount: quantity,
    productId: String(productId),
  };

  let response;
  try {
    response = await postCart(CART_ADD_URL, payload([packLine, ...giftLines]));
    // A gift that can't be added (for example, sold out) must not stop the pack itself.
    if (response.status && giftLines.length) {
      response = await postCart(CART_ADD_URL, payload([packLine]));
    }
  } catch (error) {
    deferred?.reject(error);
    if (events) {
      source.dispatchEvent(
        new events.CartErrorEvent({ error: 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' })
      );
    }
    throw new Error('We couldn’t reach the cart. Check your connection and try again.');
  }

  if (response.status) {
    const message = response.description || response.message || 'This pack couldn’t be added.';
    if (events) {
      source.dispatchEvent(
        new events.CartErrorEvent({
          error: response.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: response.description, errors: response.errors },
        })
      );
    }
    fetchCart()
      .then((cart) =>
        deferred?.resolve({
          cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { ...detail, items: cart.items, didError: true },
        })
      )
      .catch((error) => deferred?.reject(error));
    throw new Error(message);
  }

  const cart = await fetchCart().catch(() => null);

  if (deferred && events && cart) {
    deferred.resolve({
      cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { ...detail, items: cart.items, sections: response.sections, didError: false },
    });
  } else if (deferred) {
    deferred.reject(new Error('Cart refresh failed'));
  }

  // No drawer (drawer disabled, cart page, or the events module failed to load): go to the cart page.
  if (!events || !drawer) {
    window.location.href = CART_URL;
  }

  return cart;
}

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-bs-add')) return;

  const formData = new FormData(form);
  const variantId = formData.get('id');
  if (!variantId) return;

  event.preventDefault();

  const button =
    (event.submitter instanceof HTMLButtonElement && event.submitter) ||
    form.querySelector('[data-bs-add-button], button[type="submit"]');
  const errorElement = form.querySelector('[data-bs-error]');

  if (button?.getAttribute('aria-busy') === 'true') return;
  button?.setAttribute('aria-busy', 'true');
  errorElement?.setAttribute('hidden', '');

  try {
    await addToBag({
      variantId: String(variantId),
      quantity: Number(formData.get('quantity')) || 1,
      source: form,
      productId: form.dataset.productId || '',
    });
  } catch (error) {
    if (errorElement) {
      errorElement.textContent = error instanceof Error ? error.message : String(error);
      errorElement.removeAttribute('hidden');
    }
  } finally {
    button?.removeAttribute('aria-busy');
  }
});

/* ------------------------------------------------------------------ */
/* Free gifts: keep gift lines in step with the packs in the cart      */
/* ------------------------------------------------------------------ */

let syncingGifts = false;

/**
 * After the cart changes (quantity change or removal in Horizon's drawer or cart page), add any
 * missing gifts and reduce or remove gifts whose pack was reduced or removed. Only lines marked
 * with `_bs_gift` are managed, so a dish or towels bought on their own are never touched.
 */
async function syncGifts() {
  const map = giftMap();
  if (syncingGifts || !Object.keys(map).length) return;
  syncingGifts = true;

  try {
    const cart = await fetchCart();

    /** @type {Map<number, number>} */
    const wanted = new Map();
    /** @type {Map<number, string>} */
    const giftKeys = new Map();
    for (const line of cart.items) {
      for (const gift of map[String(line.variant_id)] || []) {
        wanted.set(gift.id, (wanted.get(gift.id) || 0) + line.quantity);
        giftKeys.set(gift.id, gift.key);
      }
    }

    /** @type {Record<string, number>} */
    const updates = {};
    const changedLines = [];
    /** @type {Map<number, number>} */
    const kept = new Map();
    for (const line of cart.items) {
      if (!line.properties || !line.properties._bs_gift) continue;
      const room = Math.max(0, (wanted.get(line.variant_id) || 0) - (kept.get(line.variant_id) || 0));
      const keep = Math.min(line.quantity, room);
      kept.set(line.variant_id, (kept.get(line.variant_id) || 0) + keep);
      if (keep !== line.quantity) {
        updates[line.key] = keep;
        changedLines.push({ merchandiseId: String(line.variant_id), quantity: keep });
      }
    }

    const additions = [];
    for (const [id, quantity] of wanted) {
      const missing = quantity - (kept.get(id) || 0);
      if (missing > 0) {
        additions.push({ id, quantity: missing, properties: { _bs_gift: giftKeys.get(id) } });
        changedLines.push({ merchandiseId: String(id), quantity: missing });
      }
    }

    if (!changedLines.length) return;

    const sectionIds = cartSectionIds();
    const sections = sectionIds.length ? { sections: sectionIds.join(',') } : {};
    let response = null;
    if (Object.keys(updates).length) response = await postCart(CART_UPDATE_URL, { updates, ...sections });
    if (additions.length) response = await postCart(CART_ADD_URL, { items: additions, ...sections });

    // Let Horizon's drawer, cart page and cart count re-render with the corrected cart.
    const events = await loadShopifyEvents();
    if (!events) return;
    const fresh = await fetchCart();
    const deferred = events.CartLinesUpdateEvent.createPromise();
    const event = new events.CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: changedLines,
      promise: deferred.promise,
    });
    ownEvents.add(event);
    document.dispatchEvent(event);
    deferred.resolve({
      cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(fresh),
      detail: {
        source: 'bs-gift-sync',
        sourceId: 'bs-gift-sync',
        items: fresh.items,
        sections: response && response.sections,
        didError: false,
      },
    });
  } catch (error) {
    console.warn('[brightside] Free gift sync failed:', error);
  } finally {
    syncingGifts = false;
  }
}

loadShopifyEvents().then((events) => {
  if (!events) return;
  document.addEventListener(events.StandardEvents.cartLinesUpdate, (event) => {
    if (ownEvents.has(event)) return;
    /** @type {any} */ (event).promise
      ?.then((/** @type {any} */ result) => {
        if (!result?.detail?.didError) syncGifts();
      })
      .catch(() => {});
  });
});

/** On load and on back/forward restores, fix a cart changed elsewhere (another tab, the cart page). */
function syncGiftsIfCartHasItems() {
  const count = document.querySelector('[data-bs-cart-count]')?.textContent?.trim();
  if (count && count !== '0') syncGifts();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncGiftsIfCartHasItems, { once: true });
} else {
  syncGiftsIfCartHasItems();
}

window.addEventListener('pageshow', (event) => {
  if (event.persisted) syncGiftsIfCartHasItems();
});

/* ------------------------------------------------------------------ */
/* Pack picker                                                         */
/* ------------------------------------------------------------------ */

/**
 * @typedef {object} Pack
 * @property {number} index
 * @property {number} id
 * @property {boolean} available
 * @property {string} name
 * @property {string} price
 * @property {string} compare
 * @property {string} save
 * @property {string} sticky
 * @property {{ dish: boolean, towels: boolean }} gifts
 * @property {string} giftHeading
 * @property {string} giftHeadingShort
 */

class BsPackPicker extends HTMLElement {
  /** @type {Pack[]} */
  packs = [];

  connectedCallback() {
    const data = this.querySelector('script[data-bs-packs]');
    try {
      this.packs = JSON.parse(data?.textContent || '{}').packs || [];
    } catch {
      this.packs = [];
    }

    this.selectFromUrl();
    this.addEventListener('change', this.onChange);
    // Defer one frame so the sticky bar (later in the page) is upgraded before the first sync.
    requestAnimationFrame(() => this.update());
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
  }

  /** @returns {HTMLInputElement[]} */
  get radios() {
    return /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('input[type="radio"][name="id"]')]);
  }

  /** Honour ?variant= on pages where Liquid couldn't (e.g. the homepage). */
  selectFromUrl() {
    const variant = new URLSearchParams(window.location.search).get('variant');
    if (!variant) return;
    const radio = this.radios.find((input) => input.value === variant && !input.disabled);
    if (radio) radio.checked = true;
  }

  /** @param {Event} event */
  onChange = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.name === 'id') this.update(true);
  };

  /** @returns {Pack | undefined} */
  get selectedPack() {
    const checked = this.radios.find((input) => input.checked);
    const index = Number(checked?.dataset.packIndex);
    return this.packs.find((pack) => pack.index === index);
  }

  /** @param {boolean} [userInitiated] */
  update(userInitiated = false) {
    const pack = this.selectedPack;
    if (!pack) return;

    const scope = this.closest('[data-bs-scope]') || this.closest('.shopify-section') || document;

    // Price block (product page) — anywhere in the section.
    scope.querySelectorAll('[data-bs-field="price"]').forEach((element) => {
      element.textContent = pack.price;
    });
    scope.querySelectorAll('[data-bs-field="compare"]').forEach((element) => {
      element.textContent = pack.compare;
      element.toggleAttribute('hidden', !pack.compare);
    });
    scope.querySelectorAll('[data-bs-field="save"]').forEach((element) => {
      const template = element.getAttribute('data-template') || 'Save {save}';
      element.textContent = template.replace('{save}', pack.save);
      element.toggleAttribute('hidden', !pack.save);
    });

    // Gift box.
    const giftbox = this.querySelector('[data-bs-giftbox]');
    if (giftbox) {
      const hasGift = pack.gifts.dish || pack.gifts.towels;
      giftbox.toggleAttribute('hidden', !hasGift);
      giftbox.querySelectorAll('[data-bs-gift]').forEach((element) => {
        const key = /** @type {'dish' | 'towels'} */ (element.getAttribute('data-bs-gift'));
        element.toggleAttribute('hidden', !pack.gifts[key]);
      });
      const heading = giftbox.querySelector('[data-bs-gift-heading]');
      if (heading) {
        heading.textContent =
          heading.getAttribute('data-bs-gift-heading') === 'short' ? pack.giftHeadingShort : pack.giftHeading;
      }
    }

    // Add to bag label + availability.
    const button = this.querySelector('[data-bs-add-button]');
    const label = this.querySelector('[data-bs-add-label]');
    if (button instanceof HTMLButtonElement && label) {
      const template = button.dataset.label || 'Add to bag — {price}';
      label.textContent = pack.available ? template.replace('{price}', pack.price) : 'Sold out';
      if (this.querySelector('form[data-bs-add]')) button.disabled = !pack.available;
    }
    this.querySelectorAll('[data-bs-add-compare]').forEach((element) => {
      element.textContent = pack.compare;
      element.toggleAttribute('hidden', !pack.compare || !pack.available);
    });

    // Link mode (homepage hero): go to the product page with this pack selected.
    const link = this.querySelector('[data-bs-claim-link]');
    if (link instanceof HTMLAnchorElement && pack.id) {
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('variant', String(pack.id));
      link.href = `${url.pathname}${url.search}${url.hash}`;
      const hasGift = pack.gifts.dish || pack.gifts.towels;
      const text = (!hasGift && link.dataset.labelPlain) || link.dataset.label || '';
      const linkLabel = link.querySelector('[data-bs-add-label]');
      if (linkLabel) linkLabel.textContent = text.replace('{price}', pack.price);
    }

    if (userInitiated && this.hasAttribute('data-update-url') && pack.id) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(pack.id));
      window.history.replaceState(window.history.state, '', url.toString());
    }

    this.dispatchEvent(
      new CustomEvent('bs:pack-change', {
        bubbles: true,
        detail: { pack, primary: this.hasAttribute('data-primary'), userInitiated },
      })
    );
  }
}

if (!customElements.get('bs-pack-picker')) customElements.define('bs-pack-picker', BsPackPicker);

/* ------------------------------------------------------------------ */
/* Mobile sticky bar                                                   */
/* ------------------------------------------------------------------ */

class BsStickyBar extends HTMLElement {
  connectedCallback() {
    document.addEventListener('bs:pack-change', this.onPackChange);
  }

  disconnectedCallback() {
    document.removeEventListener('bs:pack-change', this.onPackChange);
  }

  /** @param {Event} event */
  onPackChange = (event) => {
    const { pack, primary } = /** @type {CustomEvent} */ (event).detail || {};
    if (!pack || !primary) return;

    const name = this.querySelector('[data-bs-sticky-name]');
    const line = this.querySelector('[data-bs-sticky-line]');
    const price = this.querySelector('[data-bs-sticky-price]');
    const compare = this.querySelector('[data-bs-sticky-compare]');
    const input = this.querySelector('input[name="id"]');
    const button = this.querySelector('[data-bs-add-button]');

    if (name) name.textContent = pack.name;
    if (line) line.textContent = pack.sticky;
    if (price) price.textContent = pack.price;
    if (compare) {
      compare.textContent = pack.compare;
      compare.toggleAttribute('hidden', !pack.compare);
    }
    if (input instanceof HTMLInputElement) input.value = String(pack.id);
    if (button instanceof HTMLButtonElement && this.querySelector('form[data-bs-add]')) {
      button.disabled = !pack.available;
    }
  };
}

if (!customElements.get('bs-sticky-bar')) customElements.define('bs-sticky-bar', BsStickyBar);

/* ------------------------------------------------------------------ */
/* Header: cart count, cart drawer trigger, mobile menu                */
/* ------------------------------------------------------------------ */

/** @param {number} count */
function renderCartCount(count) {
  document.querySelectorAll('[data-bs-cart-count]').forEach((element) => {
    element.textContent = String(count);
  });
  document.querySelectorAll('[data-bs-cart-trigger]').forEach((element) => {
    const template = element.getAttribute('data-label-template') || 'Cart, {count} items';
    let label = template.replace('{count}', String(count));
    if (count === 1) label = label.replace(/\bitems\b/, 'item');
    element.setAttribute('aria-label', label);
  });
}

loadShopifyEvents().then((events) => {
  if (!events) return;
  document.addEventListener(events.StandardEvents.cartLinesUpdate, (event) => {
    /** @type {any} */ (event).promise
      ?.then(({ cart, detail }) => {
        const count = cart?.totalQuantity ?? detail?.itemCount;
        if (typeof count === 'number') renderCartCount(count);
      })
      .catch(() => {});
  });
});

window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  fetchCart()
    .then((cart) => renderCartCount(cart.item_count))
    .catch(() => {});
});

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  // Cart pill / icon: open Horizon's drawer instead of navigating, when the drawer exists.
  const cartTrigger = target.closest('[data-bs-cart-trigger]');
  if (cartTrigger) {
    const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
    if (drawer && typeof drawer.open === 'function') {
      event.preventDefault();
      drawer.open();
    }
    return;
  }

  // Mobile menu.
  const menuOpen = target.closest('[data-bs-menu-open]');
  if (menuOpen) {
    const dialog = document.getElementById(menuOpen.getAttribute('aria-controls') || '');
    if (dialog instanceof HTMLDialogElement) {
      dialog.showModal();
      menuOpen.setAttribute('aria-expanded', 'true');
      dialog.addEventListener(
        'close',
        () => {
          menuOpen.setAttribute('aria-expanded', 'false');
        },
        { once: true }
      );
    }
    return;
  }

  const menuDialog = target.closest('dialog[data-bs-menu]');
  if (menuDialog instanceof HTMLDialogElement) {
    // Close on the close button, on any link (anchors on this page), or on a backdrop click.
    if (target.closest('[data-bs-menu-close]') || target.closest('a') || target === menuDialog) {
      menuDialog.close();
    }
  }
});

/* ------------------------------------------------------------------ */
/* Hero demo video                                                     */
/* ------------------------------------------------------------------ */

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-bs-play]') : null;
  if (!button) return;
  const media = button.closest('[data-bs-video]');
  const template = media?.querySelector('template');
  if (!media || !(template instanceof HTMLTemplateElement)) return;

  const fragment = template.content.cloneNode(true);
  const video = /** @type {DocumentFragment} */ (fragment).querySelector('video');
  media.querySelectorAll('[data-bs-poster]').forEach((element) => element.remove());
  button.remove();
  media.appendChild(fragment);
  if (video) {
    video.setAttribute('controls', '');
    video.play().catch(() => {});
    video.focus();
  }
});

/* ------------------------------------------------------------------ */
/* Product gallery                                                     */
/* ------------------------------------------------------------------ */

class BsGallery extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  observer = null;

  connectedCallback() {
    this.track = this.querySelector('[data-bs-gallery-track]');
    this.slides = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-bs-slide]')]);
    this.counter = this.querySelector('[data-bs-gallery-counter]');
    this.zoomDialog = this.querySelector('dialog[data-bs-zoom]');

    this.addEventListener('click', this.onClick);
    document.addEventListener('bs:pack-change', this.onPackChange);

    if (this.track && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
              this.setActive(this.slides.indexOf(/** @type {HTMLElement} */ (entry.target)), false);
            }
          }
        },
        { root: this.track, threshold: [0.6] }
      );
      this.slides.forEach((slide) => this.observer?.observe(slide));
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('bs:pack-change', this.onPackChange);
    this.observer?.disconnect();
  }

  /**
   * The free-gift badge on the main image follows the selected pack, so the Starter shows the plain image.
   * @param {Event} event
   */
  onPackChange = (event) => {
    const { pack, primary, userInitiated } = /** @type {CustomEvent} */ (event).detail || {};
    if (!pack || !primary) return;

    const badge = this.querySelector('[data-bs-gift-badge]');
    if (badge) {
      const count = Number(Boolean(pack.gifts.dish)) + Number(Boolean(pack.gifts.towels));
      badge.toggleAttribute('hidden', count === 0);
      badge.querySelectorAll('[data-bs-gift]').forEach((element) => {
        const key = /** @type {'dish' | 'towels'} */ (element.getAttribute('data-bs-gift'));
        element.toggleAttribute('hidden', !pack.gifts[key]);
      });
      const label = badge.querySelector('[data-bs-gift-badge-label]');
      const template = badge.getAttribute(count > 1 ? 'data-label-many' : 'data-label-one') || '';
      if (label) label.textContent = template.replace('{count}', String(count));
    }

    // Show the main image when someone picks a pack.
    if (userInitiated) this.setActive(0);
  };

  get isCarousel() {
    return window.matchMedia('(max-width: 860px)').matches;
  }

  /**
   * @param {number} index
   * @param {boolean} [scroll]
   */
  setActive(index, scroll = true) {
    if (index < 0 || !this.slides?.length) return;
    this.activeIndex = index;

    this.slides.forEach((slide, i) => slide.toggleAttribute('data-active', i === index));
    this.querySelectorAll('[data-bs-thumb]').forEach((thumb) => {
      thumb.setAttribute('aria-pressed', String(Number(thumb.getAttribute('data-bs-thumb')) === index));
    });
    if (this.counter) this.counter.textContent = `${index + 1} / ${this.slides.length}`;

    if (scroll && this.isCarousel && this.track) {
      const slide = this.slides[index];
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: reduced ? 'auto' : 'smooth' });
    }
  }

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const thumb = target.closest('[data-bs-thumb]');
    if (thumb) {
      this.setActive(Number(thumb.getAttribute('data-bs-thumb')));
      return;
    }

    if (target.closest('[data-bs-zoom-open]') && this.zoomDialog instanceof HTMLDialogElement) {
      const slide = this.slides?.[this.activeIndex ?? 0];
      const body = this.zoomDialog.querySelector('[data-bs-zoom-body]');
      if (slide && body) {
        // Show a copy of the current slide (image or before/after split) at full size.
        const copy = /** @type {HTMLElement} */ (slide.cloneNode(true));
        copy.removeAttribute('data-bs-slide');
        copy.removeAttribute('data-active');
        copy.removeAttribute('role');
        copy.removeAttribute('aria-roledescription');
        copy.removeAttribute('aria-label');
        copy.querySelectorAll('img').forEach((image) => {
          image.setAttribute('sizes', '92vw');
          image.setAttribute('loading', 'eager');
        });
        body.replaceChildren(copy);
      }
      this.zoomDialog.showModal();
      return;
    }

    if (this.zoomDialog && (target.closest('[data-bs-zoom-close]') || target === this.zoomDialog)) {
      /** @type {HTMLDialogElement} */ (this.zoomDialog).close();
    }
  };
}

if (!customElements.get('bs-gallery')) customElements.define('bs-gallery', BsGallery);

/* ------------------------------------------------------------------ */
/* "Ships by" line: the next business day in the store's time zone      */
/* ------------------------------------------------------------------ */

/**
 * US federal holidays that carriers don't ship on (fixed dates, not the observed weekday).
 * @param {Date} date - A UTC calendar date.
 */
function isShippingHoliday(date) {
  const month = date.getUTCMonth();
  const day = date.getUTCDate();
  const weekday = date.getUTCDay();
  const nth = Math.ceil(day / 7);
  const daysInMonth = new Date(Date.UTC(date.getUTCFullYear(), month + 1, 0)).getUTCDate();
  const isLast = day + 7 > daysInMonth;
  return (
    (month === 0 && day === 1) || // New Year's Day
    (month === 4 && weekday === 1 && isLast) || // Memorial Day
    (month === 6 && day === 4) || // Independence Day
    (month === 8 && weekday === 1 && nth === 1) || // Labor Day
    (month === 10 && weekday === 4 && nth === 4) || // Thanksgiving
    (month === 11 && day === 25) // Christmas
  );
}

/** @param {string} timeZone */
function nextBusinessDay(timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: 'numeric', day: 'numeric' })
    .formatToParts(new Date());
  /** @param {string} type */
  const part = (type) => Number(parts.find((p) => p.type === type)?.value);
  const date = new Date(Date.UTC(part('year'), part('month') - 1, part('day')));
  do {
    date.setUTCDate(date.getUTCDate() + 1);
  } while (date.getUTCDay() === 0 || date.getUTCDay() === 6 || isShippingHoliday(date));
  return date;
}

function renderShipsBy() {
  document.querySelectorAll('[data-bs-ships-by]').forEach((element) => {
    const text = element.querySelector('[data-bs-ships-by-text]');
    if (!text) return;
    try {
      const date = nextBusinessDay(element.getAttribute('data-time-zone') || 'America/New_York');
      const formatted = new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      }).format(date);
      text.textContent = (element.getAttribute('data-template') || 'Ships by {date}').replace('{date}', formatted);
      element.removeAttribute('hidden');
    } catch {
      // Leave the line hidden if the browser can't work out the date.
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', renderShipsBy, { once: true });
} else {
  renderShipsBy();
}
